import { initialsOf } from '@liniya/client-core';
import { Megaphone, Users } from 'lucide-react';

export { initialsOf };

type Size = 'small' | 'default' | 'medium' | 'large';

interface Props {
  name: string;
  kind?: 'direct' | 'group' | 'channel';
  size?: Size;
  imageUrl?: string | null;
}

const SIZE_CLASS: Record<Size, string> = {
  small: 'avatar small',
  default: 'avatar',
  medium: 'avatar medium',
  large: 'avatar large',
};

const ICON_SIZE: Record<Size, number> = {
  small: 18,
  default: 21,
  medium: 26,
  large: 34,
};

/**
 * Identity mark for a person, group or channel.
 *
 * Channels take an icon rather than initials because a channel is a
 * publication, not a person, and the difference has to be legible in a list
 * scanned at a glance. The element is hidden from assistive technology: the
 * name it stands for is always written next to it, and reading it twice adds
 * nothing.
 */
export function Avatar({ name, kind = 'direct', size = 'default', imageUrl }: Props) {
  const className = kind === 'channel' ? `${SIZE_CLASS[size]} channel-avatar` : SIZE_CLASS[size];

  if (imageUrl) {
    return (
      <span className={className}>
        <img src={imageUrl} alt="" loading="lazy" decoding="async" />
      </span>
    );
  }

  return (
    <span className={className} aria-hidden="true">
      {kind === 'channel' ? (
        <Megaphone size={ICON_SIZE[size]} />
      ) : kind === 'group' ? (
        <Users size={ICON_SIZE[size]} />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
}
